/**
 * 3-layer schema validation for LLM JSON output.
 *
 * Layer 1: Provider-native structured output (eliminates most failures)
 * Layer 2: Self-correction re-prompt (ask the LLM to fix its own output)
 * Layer 3: parseLlmJson fallback (progressive repair)
 */

import { parseLlmJson } from "./jsonRepair";

/** Validation result from the schema validation pipeline. */
export interface ValidationResult<T> {
  data: T;
  layer: 1 | 2 | 3;
  repaired: boolean;
}

/** Schema validator function type. */
export type SchemaValidator<T> = (data: unknown) => data is T;

/**
 * Validate and extract JSON from LLM output with 3-layer defense.
 *
 * @param raw - Raw LLM output string
 * @param validator - Optional type guard for runtime validation
 * @param selfCorrect - Optional async function to re-prompt the LLM for correction
 * @returns Validated data with metadata about which layer succeeded
 */
export async function validateLlmJson<T>(
  raw: string,
  validator?: SchemaValidator<T>,
  selfCorrect?: (failedOutput: string, error: string) => Promise<string>,
): Promise<ValidationResult<T>> {
  // Layer 1: Direct parse (covers provider-native structured output)
  try {
    const parsed = JSON.parse(raw) as T;
    if (!validator || validator(parsed)) {
      return { data: parsed, layer: 1, repaired: false };
    }
  } catch {
    // continue to layer 2
  }

  // Layer 2: Self-correction re-prompt (if available)
  if (selfCorrect) {
    try {
      const corrected = await selfCorrect(raw, "Invalid JSON or schema mismatch");
      const parsed = parseLlmJson<T>(corrected);
      if (!validator || validator(parsed)) {
        return { data: parsed, layer: 2, repaired: true };
      }
    } catch {
      // continue to layer 3
    }
  }

  // Layer 3: Progressive repair via parseLlmJson
  const parsed = parseLlmJson<T>(raw);
  if (validator && !validator(parsed)) {
    throw new Error("Schema validation failed after all repair attempts");
  }
  return { data: parsed, layer: 3, repaired: true };
}

// Common validators for game data types

/** Validate a beat extraction result. */
export function isBeatResult(data: unknown): data is BeatResult {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.outcome === "string" &&
    Array.isArray(d.consequences) &&
    typeof d.mood === "string" &&
    Array.isArray(d.npcs_involved) &&
    Array.isArray(d.tags)
  );
}

/** Validate an options array. */
export function isOptionsArray(data: unknown): data is OptionItem[] {
  if (!Array.isArray(data)) return false;
  return data.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as Record<string, unknown>).label === "string" &&
      typeof (item as Record<string, unknown>).text === "string" &&
      typeof (item as Record<string, unknown>).category === "string",
  );
}

/** Validate a mechanics result. */
export function isMechanicsResult(data: unknown): data is MechanicsResultSchema {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return typeof d.rollNeeded === "boolean" && typeof d.skill === "string";
}

/** Validate a character assignment. */
export function isCharacterAssignment(data: unknown): data is CharacterAssignmentSchema {
  if (!data || typeof data !== "object") return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.name === "string" &&
    typeof d.st === "number" &&
    typeof d.dx === "number" &&
    typeof d.iq === "number" &&
    typeof d.ht === "number" &&
    typeof d.skills === "object" &&
    d.skills !== null
  );
}

/** Validate a backgrounds array. */
export function isBackgroundsArray(data: unknown): data is BackgroundSchema[] {
  if (!Array.isArray(data)) return false;
  return data.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      typeof (item as Record<string, unknown>).name === "string" &&
      typeof (item as Record<string, unknown>).description === "string",
  );
}

// Type interfaces for validators

/** Beat extraction result shape. */
interface BeatResult {
  outcome: string;
  consequences: string[];
  mood: string;
  npcs_involved: string[];
  tags: string[];
  arcs_advanced?: string[];
}

/** Option item shape. */
interface OptionItem {
  label: string;
  text: string;
  category: string;
}

/** Mechanics result shape for validation. */
interface MechanicsResultSchema {
  rollNeeded: boolean;
  skill: string;
  targetNumber?: number;
  modifiers?: unknown[];
}

/** Character assignment shape for validation. */
interface CharacterAssignmentSchema {
  name: string;
  st: number;
  dx: number;
  iq: number;
  ht: number;
  skills: Record<string, number>;
  rationale?: string;
}

/** Background option shape for validation. */
interface BackgroundSchema {
  name: string;
  description: string;
}
